import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { fromEvent } from 'file-selector'
import { COMMON_MIME_TYPES } from 'file-selector/mime'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

import { convertFileSize } from 'utils/strings'
import colors from 'components/utils/colors'
import Icon from 'components/Shared/Icon/Index'

// file-selector bundles only ~37 extensions by default, the full table is needed
// to resolve a mime type for files the browser reports without one
const getFilesFromEvent = (event) => fromEvent(event, { mimeTypes: COMMON_MIME_TYPES })

const STRIPES_COLOR = '#00000012'
const STRIPES_WIDTH_PERCENT = 5
const STRIPES_BOX_SIZE_PX = 5

const StyledWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  padding: 4px;
  outline: none;
  user-select: none;
  cursor: pointer;
  background-image: linear-gradient(
    135deg,
    ${STRIPES_COLOR} 0%,
    ${STRIPES_COLOR} ${STRIPES_WIDTH_PERCENT}%,
    transparent ${STRIPES_WIDTH_PERCENT}%,
    transparent 50%,
    ${STRIPES_COLOR} 50%,
    ${STRIPES_COLOR} ${50 + STRIPES_WIDTH_PERCENT}%,
    transparent ${50 + STRIPES_WIDTH_PERCENT}%,
    transparent
  );
  background-size: ${STRIPES_BOX_SIZE_PX}px ${STRIPES_BOX_SIZE_PX}px;
`

const DropZone = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.secondary70};
  padding: 20px;

  ${props => props.isUploadFailed && css`
    border-color: ${colors.alert70};
  `}
`

const AddFileIcon = styled(Icon)`
  margin-right: 4px;
`

const TitleLine = styled.div`
  display: flex;
  font-size: 14px;
  font-weight: 600;
  align-items: center;
`

const MaxUploadInfo = styled.p`
  font-size: 12px;
  padding-top: 10px;
`

const DropzoneFileInput = ({ onChange, title, accept, maxSize, onError, isUploadFailed }) => {
  const { t } = useTranslation()
  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept,
    maxSize,
    getFilesFromEvent,
    onDrop: (filesToUpload) => onChange(filesToUpload),
    onDropRejected: onError,
    multiple: false,
  })

  const maxSizeText = convertFileSize(maxSize)
  return (
    <StyledWrapper {...getRootProps()}>
      <DropZone isUploadFailed={isUploadFailed}>
        <input {...getInputProps()} />
        <TitleLine>
          <AddFileIcon iconName='icon-add-big-16' />
          <p>{title || t('dropzoneFileInput.placeholder')}</p>
        </TitleLine>
        {maxSize && <MaxUploadInfo>{t('dropzoneFileInput.maxSizeInfo', { maxSizeText })}</MaxUploadInfo>}
      </DropZone>
    </StyledWrapper>
  )
}

DropzoneFileInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  accept: PropTypes.object,
  maxSize: PropTypes.number,
  onError: PropTypes.func,
  isUploadFailed: PropTypes.bool,
  children: PropTypes.node,
}

export default DropzoneFileInput
